/* eslint-disable @typescript-eslint/no-explicit-any */
import appLocalStorage from '@/features/session/store/appLocalStorage';
import { Roles, RolesEnv } from './enums';
import {
  rolelist,
  type UserValidation,
} from '@/features/session/types/session.types';
import type { ApiError, RolesEnvType } from './types';
import { isAxiosError } from 'axios';

export const getClassNames = (...classes: string[]): string =>
  classes.filter(Boolean).join(' ');

// Utility function to get the user's role based on environment and access profile
// If a user object is provided, uses that; otherwise, fetches from local storage
// Returns the highest priority role for the current environment, or UNKNOWN if not found
export const getRole = (user: UserValidation): Roles => {
  let userAccess;

  // Use provided user or get from local storage
  if (user) {
    userAccess = user;
  } else {
    userAccess = appLocalStorage.getUserAccessProfile() as UserValidation;
  }

  if (userAccess) {
    // Destructure access roles from user profile
    const {
      roles: { access },
    } = userAccess;

    // Determine current environment (PROD or TEST)
    const currentRoleEnv: RolesEnvType =
      import.meta.env.VITE_APP_ENV === RolesEnv.PROD
        ? RolesEnv.PROD
        : RolesEnv.TEST;

    // Find access roles for the current environment
    const currentAccessRoles = access.find(
      accessRoles => accessRoles.env === currentRoleEnv
    )?.roles;

    // If only one role, return it; otherwise, return the highest priority role
    const role =
      currentAccessRoles?.length === 1
        ? currentAccessRoles[0].role
        : currentAccessRoles?.sort(
            (a, b) =>
              (rolelist.find(e => e.role === a.role)?.priority || 0) -
              (rolelist.find(e => e.role === b.role)?.priority || 1)
          )[0]?.role;

    return (role as Roles) || Roles.UNKNOWN;
  }

  // Return UNKNOWN if no user or access found
  return Roles.UNKNOWN;
};

export const createQueryString = (params: Record<string, any>): string => {
  const filteredParams = Object.entries(params).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = String(value);
      }
      return acc;
    },
    {}
  );

  if (Object.keys(filteredParams).length === 0) {
    return '';
  }

  return `?${new URLSearchParams(filteredParams).toString()}`;
};

export const replacePlaceholders = (
  inputString: string,
  values: Record<string, string | number>
): string => {
  return inputString.replace(/:(\w+)/g, (match, key) => {
    const value = values[key];
    return value !== undefined && value !== null ? String(value) : match;
  });
};

export const formatAPIError = (error: unknown): ApiError => {
  const apiError: ApiError = {
    message: 'Something went wrong',
    status: 500,
  };
  if (isAxiosError(error)) {
    const responseData = error.response?.data as
      | { message?: string; error?: string }
      | string
      | undefined;

    const backendMessage =
      typeof responseData === 'string'
        ? responseData
        : responseData?.message || responseData?.error;

    apiError.message =
      backendMessage || error.message || 'Something went wrong';
    apiError.status = error.response?.status || 500;
  } else {
    apiError.message = (error as Error)?.message || 'Unknown error occurred';
  }
  return apiError;
};

type FormatDateOptions = {
  withoutTime?: boolean;
  separator?: string;
};

export const formatDate = (
  date: Date | string | number,
  { withoutTime = false, separator = '.' }: FormatDateOptions = {}
): string => {
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();
  const hours = String(parsedDate.getHours()).padStart(2, '0');
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0');

  return withoutTime
    ? `${day}${separator}${month}${separator}${year}`
    : `${day}${separator}${month}${separator}${year}, ${hours}:${minutes}`;
};

export const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const dateToUTCISOString = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(utcDate.getTime()) ? '' : utcDate.toISOString();
};

/**
 * Combines a date string (YYYY-MM-DD) and a local time string (HH:MM) into a UTC ISO 8601 string.
 * The date and time are interpreted as local time and converted to UTC.
 */
export const dateTimeToUTCISOString = (date: string, time: string): string => {
  if (!date || !time) return '';
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const localDate = new Date(year, month - 1, day, hours, minutes);
  return Number.isNaN(localDate.getTime()) ? '' : localDate.toISOString();
};
